import Data from 'data';
import { isSubType } from 'logic/itemTypes';

import { defaultAnimation } from './animation';
import { DamageCalculator, missileDamage } from './damage';
import { defaultDPS } from './dps';
import skillFunctions from './functions';

export function defaultInfo(id, stats, level, target) {
  const skill = Data.skills[id];
  const missile = Data.missileByName[skill.srvmissile];
  const animation = this.animation ? this.animation(skill, stats, level) :
    (missile || this.damage) && defaultAnimation(skill, stats);
  if (missile) {
    return {
      damage: missileDamage(missile, stats, stats.weapon1, level, target),
      animation,
    };
  } else if (this.damage) {
    const weapsel = (skill.srvdofunc === 1 ? 3 : skill.weapsel);
    let damage = this.damage(skill, stats, weapsel === 1 ? stats.weapon2 : stats.weapon1, level, target);
    if (damage && damage instanceof DamageCalculator) {
      damage = {damage};
    }
    if ((weapsel === 3 || weapsel === 2) && stats.weapon2) {
      if (!damage) damage = {};
      const offhand = this.damage(skill, stats, stats.weapon2, level, target);
      if (offhand && offhand instanceof DamageCalculator) {
        damage.offhand = offhand;
      } else if (offhand) {
        damage.offhand = offhand.damage;
      }
    }
    return {
      damage,
      animation,
    };
  } else if (animation) {
    return {animation};
  }
}

export const itemSkillTypes = {
  aura: {
    stat: 'item_aura',
    format: 'Item Aura',
    levelValue: true,
  },
  charged: {
    stat: 'item_charged_skill',
    format: '{0} Charges',
  },
  attack: {
    stat: 'item_skillonattack',
    format: '{0}% Chance on attack',
    proc: true,
  },
  kill: {
    stat: 'item_skillonkill',
    format: '{0}% Chance when you Kill an Enemy',
    proc: true,
  },
  death: {
    stat: 'item_skillondeath',
    format: '{0}% Chance when you Die',
    proc: true,
  },
  hit: {
    stat: 'item_skillonhit',
    format: '{0}% Chance on striking',
    proc: true,
  },
  level: {
    stat: 'item_skillonlevelup',
    format: '{0}% Chance when you Level-Up',
    proc: true,
  },
  gethit: {
    stat: 'item_skillongethit',
    format: '{0}% Chance when struck',
    proc: true,
  },
};

export function skillDisabled(skill, stats, target) {
  if (stats.unit !== 'player') return false;
  if (skill.aura) return false;

  if (skill.restrict === 2) {
    const forms = [skill.state1, skill.state2, skill.state3].filter(Boolean);
    if (!forms.some(state => stats.base.state?.[state])) return 'form';
  } else if (!skill.restrict) {
    if (stats.base.state && Object.keys(stats.base.state).some(state => Data.states[state]?.restrict)) return 'human';
  }

  const func = skillFunctions[skill.srvdofunc];
  if (func?.checkTarget && !func.checkTarget(target)) {
    return 'target';
  }

  function checkWeapon(weapon, include, exclude, defaultType) {
    const type = Data.items[weapon?.baseitem]?.type || defaultType;
    if (!type) return false;
    if (!include.some(t => t && isSubType(type, t))) return false;
    if (exclude.some(t => t && isSubType(type, t))) return false;
    return true;
  }

  if (skill.weapsel === 1) {
    if (skill.itypea1 && !checkWeapon(stats.weapon2, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2])) return 'offhand';
  } else if (skill.weapsel === 3) {
    if (skill.itypea1 && !checkWeapon(stats.weapon1, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2], 'weap')) return 'weapon';
    if (skill.itypeb1 && !checkWeapon(stats.weapon2, [skill.itypeb1, skill.itypeb2, skill.itypeb3], [skill.etypeb1, skill.etypeb2])) return 'offhand';
  } else if (skill.itypea1 === 'shld') {
    if (!stats.shieldbase) return 'weapon';
  } else if (skill.itypea1) {
    if (!checkWeapon(stats.weapon1, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2], 'weap')) return 'weapon';
  }
}

export function allSkillsInfo(allStats) {
  const result = {};
  for (const [unit, stats] of Object.entries(allStats)) {
    const skills = [];
    const target = (unit === 'monster' ? allStats.player : allStats.monster);
    function add(key, id, props) {
      if (skills.some(s => s.key === key)) return;
      const skill = Data.skills[id];
      const func = skillFunctions[skill?.srvdofunc || 0];
      if (!skill || !func) return;
      const level = props?.itemLevel || stats.skills[id];
      const disabled = skillDisabled(skill, stats, target);
      if (!skill.charclass && disabled) return;
      const info = (func.info || defaultInfo).call(func, id, stats, level, target, allStats);
      const isBuff = (func.showBuff ? func.showBuff(id, stats, level) : func.buff);
      if (info || func.showActive?.(id) || isBuff || (unit === 'player' && func.summon)) {
        skills.push({key, id, info, ...props});
      }
    }
    for (const id in stats.skills) {
      add(`${unit}${id}`, id);
    }
    for (const [type, {stat, levelValue}] of Object.entries(itemSkillTypes)) {
      for (const values of [stats.base[stat], stats.weapon1[stat], stats.weapon2?.[stat]].filter(Boolean)) {
        for (const [arg, value] of Object.entries(values)) {
          if (levelValue) {
            add(`${unit}${stat}#${arg}`, arg, {itemType: type, itemParam: 0, itemLevel: value});
          } else {
            const [id, level] = arg.split('#');
            add(`${unit}${stat}#${arg}`, id, {itemType: type, itemParam: value, itemLevel: parseInt(level)});
          }
        }
      }
    }
    result[unit] = {
      target,
      skills,
    };
  }
  for (const [unit, {target, skills}] of Object.entries(result)) {
    const stats = allStats[unit];
    for (const entry of skills) {
      const {id, info, itemLevel} = entry;
      const skill = Data.skills[id];
      const func = skillFunctions[skill.srvdofunc || 0];
      const level = itemLevel || stats.skills[id];

      const dps = (func.dps || defaultDPS).call(func, id, stats, level, target, info, result, allStats);
      if (dps) entry.info = {...info, dps};
    }
  }
  return result;
}
